"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { primaryNav, adminNav, type NavItem } from "./nav-config";

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  isStaff?: boolean;
}

const rowBase =
  "flex h-10 items-center gap-3 rounded-md px-3 text-body-sm font-medium transition-colors duration-fast ease-out " +
  "focus-visible:outline-none focus-visible:ring focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface";

function useActive(href: string): boolean {
  const pathname = usePathname();
  return pathname === href || pathname.startsWith(href + "/");
}

function NavLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const active = useActive(item.href);
  const Icon = item.icon;
  return (
    <Link
      href={item.href}
      title={collapsed ? item.label : undefined}
      aria-current={active ? "page" : undefined}
      className={cn(
        rowBase,
        collapsed && "justify-center px-0",
        active ? "bg-blue-50 text-primary dark:bg-primary/15" : "text-foreground-secondary hover:bg-subtle hover:text-foreground"
      )}
    >
      <Icon className="size-5 shrink-0" aria-hidden="true" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </Link>
  );
}

/** Mục có submenu: cha bấm để mở/gập; thu gọn thì link thẳng tới mục con đầu. */
function NavGroup({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const pathname = usePathname();
  const children = item.children ?? [];
  const anyActive = children.some((c) => pathname === c.href || pathname.startsWith(c.href + "/"));
  const [open, setOpen] = React.useState(anyActive);
  React.useEffect(() => {
    if (anyActive) setOpen(true);
  }, [anyActive]);

  if (collapsed) {
    const first = children[0] ?? item;
    return <NavLink item={{ ...item, href: first.href }} collapsed />;
  }

  const Icon = item.icon;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          rowBase,
          "w-full",
          anyActive ? "text-primary" : "text-foreground-secondary hover:bg-subtle hover:text-foreground"
        )}
      >
        <Icon className="size-5 shrink-0" aria-hidden="true" />
        <span className="flex-1 truncate text-left">{item.label}</span>
        <ChevronDown className={cn("size-4 shrink-0 transition-transform", open && "rotate-180")} aria-hidden="true" />
      </button>
      {open && (
        <div className="mt-1 space-y-1 pl-4">
          {children.map((c) => (
            <NavLink key={c.href} item={c} collapsed={false} />
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ collapsed, onToggleCollapse, isStaff = false }: SidebarProps) {
  return (
    <div className="flex h-full flex-col border-r border-border bg-surface">
      {/* Brand + nút thu gọn (3 gạch) */}
      <div
        className={cn(
          "flex h-topbar shrink-0 items-center gap-2 border-b border-border px-3",
          collapsed && "justify-center px-0"
        )}
      >
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Mở rộng thanh bên" : "Thu gọn thanh bên"}
          className="grid size-9 shrink-0 place-items-center rounded-md text-foreground-secondary transition-colors hover:bg-subtle hover:text-foreground focus-visible:outline-none focus-visible:ring focus-visible:ring-ring"
        >
          <Menu className="size-5" aria-hidden="true" />
        </button>
        {!collapsed && (
          <span className="truncate text-body font-bold text-foreground">Cô Giáo Tiểu Học</span>
        )}
      </div>

      {/* Điều hướng.
          - Tài khoản quản trị (staff): CHỈ hiện menu quản trị, ẩn các tính năng của user.
          - Tài khoản thường: hiện menu tính năng như bình thường. */}
      <nav className="flex-1 space-y-1 overflow-y-auto p-3" aria-label="Điều hướng chính">
        {isStaff ? (
          <>
            {!collapsed && (
              <p className="px-3 pb-1 text-caption font-semibold uppercase tracking-wide text-muted">Quản trị</p>
            )}
            {adminNav.map((item) => (
              <NavLink key={item.href} item={item} collapsed={collapsed} />
            ))}
          </>
        ) : (
          primaryNav.map((item) =>
            item.children ? (
              <NavGroup key={item.label} item={item} collapsed={collapsed} />
            ) : (
              <NavLink key={item.href} item={item} collapsed={collapsed} />
            )
          )
        )}
      </nav>
    </div>
  );
}
